import React, { FC, MouseEvent, KeyboardEvent, isValidElement } from 'react';
import { IconProps, Icon } from '../components/Icon';
import { Box, ButtonGroup, CloseButton, HStack, useSlotRecipe } from '@chakra-ui/react';
import { Button } from '../components/Button';
import { Typography } from '../components/Typography';

type AlertVariants = 'success' | 'error' | 'info' | 'warning' | 'neutral';

export type Props = {
  isClosable?: boolean;
  variant: AlertVariants;
  title?: string;
  description: string | React.JSX.Element;
  actions?: {
    primary?: {
      label: string;
      onClick: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
    };
    secondary?: {
      label: string;
      onClick: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
    };
  };
  onClose?: (e?: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
  showIcon?: boolean;
  role?: 'alert' | 'status' | 'none';
};

export const Alert: FC<Props> = ({ variant, title, description, actions, isClosable, onClose, showIcon, role }) => {
  const variantToIcon: Record<AlertVariants, IconProps> = {
    success: {
      prefix: 'fas',
      iconName: 'check-circle',
    },
    error: {
      prefix: 'fas',
      iconName: 'octagon-exclamation',
    },
    info: {
      prefix: 'fas',
      iconName: 'info-circle',
    },
    warning: {
      prefix: 'fas',
      iconName: 'exclamation-triangle',
    },
    neutral: {
      prefix: 'far',
      iconName: 'bell',
    },
  };

  /**
   * Hooks
   */
  const styles = useSlotRecipe({ key: 'alert' })({ variant: variant as never }) as Record<string, any>;
  /**
   * DOM
   */
  return (
    <HStack
      data-object-type="alert"
      role={role === 'none' ? undefined : role}
      align="flex-start"
      css={styles.root}
      display="inline-flex"
    >
      {showIcon && (
        <Box
          mt={-0.5}
          css={{
            '& > span': { ...styles.indicator, display: 'grid' },
          }}
        >
          <Icon {...variantToIcon[variant]} />
        </Box>
      )}
      <Box flexGrow={1} mt={-0.5}>
        {title && (
          <Box css={styles.title}>
            <Typography textStyle={'ui-default'} as={'h2'}>
              {title}
            </Typography>
          </Box>
        )}
        <Box css={styles.description}>
          {(isValidElement(description) && description) || <Typography textStyle={'p2'}>{description}</Typography>}
        </Box>
        {(actions?.primary || actions?.secondary) && (
          <ButtonGroup gap={'xs'} mt={'sm'} colorPalette={'gray'} size={'sm'} justifyContent={'end'}>
            {actions.primary && (
              <Button variant={'solid'} onClick={actions.primary.onClick}>
                {actions.primary.label}
              </Button>
            )}
            {actions.secondary && (
              <Button variant={'outline'} onClick={actions.secondary.onClick}>
                {actions.secondary.label}
              </Button>
            )}
          </ButtonGroup>
        )}
      </Box>
      {isClosable && (
        <CloseButton
          size={'2xs'}
          onClick={onClose}
          colorPalette={variant}
          variant={'ghost'}
          data-action="dismiss-alert"
        />
      )}
    </HStack>
  );
};
